import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = { kode: "", nama: "", subdomain: "", admin_nomor: "", admin_nama: "", admin_password: "" };

export default function AreaFormDialog({ open, onOpenChange, area, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setErr(""); setF(area ? { ...EMPTY, kode: area.kode, nama: area.nama, subdomain: area.subdomain || "" } : EMPTY); } }, [open, area]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      if (area) await api.put(`/master/areas/${area.kode}`, { nama: f.nama });
      else await api.post("/master/areas", { ...f, subdomain: "" });
      toast.success(area ? "Area diperbarui" : `Area ${f.kode.toUpperCase()} dibuat`);
      onSaved(); onOpenChange(false);
    } catch (ex) { setErr(ex.response?.data?.detail || "Gagal menyimpan area"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="area-form-dialog">
        <DialogHeader>
          <DialogTitle>{area ? `Ubah Area ${area.kode}` : "Tambah Area Baru"}</DialogTitle>
          <DialogDescription>{area ? "Ubah nama tampilan area." : "Area baru mendapat database terpisah dan admin awal sendiri. Pengguna memilih area ini saat login."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Kode Area *</Label><Input value={f.kode} onChange={(e) => set("kode", e.target.value.toUpperCase())} disabled={!!area} placeholder="REGIONAL2" required data-testid="area-kode" /></div>
            <div className="space-y-1.5"><Label>Nama Area *</Label><Input value={f.nama} onChange={(e) => set("nama", e.target.value)} placeholder="REGIONAL 2" required data-testid="area-nama" /></div>
          </div>
          {/* Mode subdomain diarsipkan: akses area memakai pemilih Area di halaman login */}
          {!area && (
            <fieldset className="rounded-lg border border-slate-200 p-3 space-y-3">
              <legend className="px-1 text-xs font-bold text-slate-600">Admin awal area (opsional, bisa dibuat nanti)</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>No. Pegawai Admin</Label><Input value={f.admin_nomor} onChange={(e) => set("admin_nomor", e.target.value.toUpperCase())} placeholder="ADMIN-R2" data-testid="area-admin-nomor" /></div>
                <div className="space-y-1.5"><Label>Nama Admin</Label><Input value={f.admin_nama} onChange={(e) => set("admin_nama", e.target.value)} data-testid="area-admin-nama" /></div>
              </div>
              <div className="space-y-1.5"><Label>Kata Sandi Admin</Label><Input type="password" value={f.admin_password} onChange={(e) => set("admin_password", e.target.value)} placeholder="min. 6 karakter" data-testid="area-admin-password" /></div>
            </fieldset>
          )}
          {err && <p className="text-sm text-rose-600" data-testid="area-form-error">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="area-form-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{area ? "Simpan" : "Buat Area"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
